/**
 * Helpers callable Firebase pour le module Partenaires.
 * Wraps createPartnerCheckout (NestJS) + updatePartnerAddons (Firebase Callable).
 */
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, app } from "./firebase";
import { API_CONFIG } from "@/config/api.config";

const functions = getFunctions(app, "us-central1");

export type PartnerAddonSlug =
  | "business_law"
  | "immigration_law"
  | "notary"
  | "cpa"
  | "tax_advisor";

export const PARTNER_ADDON_SLUGS: readonly PartnerAddonSlug[] = [
  "business_law",
  "immigration_law",
  "notary",
  "cpa",
  "tax_advisor",
] as const;

export const PARTNER_ADDON_LABELS: Record<PartnerAddonSlug, string> = {
  business_law: "Business Law",
  immigration_law: "Immigration Law",
  notary: "Notary",
  cpa: "CPA",
  tax_advisor: "Tax Advisor",
};

export const PARTNER_BASE_AMOUNT_USD = 99;
export const PARTNER_ADDON_AMOUNT_USD = 49;

export const PARTNER_PLANS = {
  basic: {
    priceId: 'price_1TczgCCkUJGKtqQAoO94CRJL',
    price: 99,
    regularPrice: 149,
    name: { en: 'Basic', fr: 'Basic' },
    features: {
      en: ['1 professional role included', 'Internal partner dashboard', 'Client directory', 'Secure messaging', 'Calendar + Video conferencing'],
      fr: ['1 rôle professionnel inclus', 'Tableau de bord partenaire', 'Annuaire clients', 'Messagerie sécurisée', 'Calendrier + Visioconférence'],
    },
  },
  pro: {
    priceId: 'price_1TczhcCkUJGKtqQASkJv7VGg',
    price: 199,
    regularPrice: 299,
    name: { en: 'Pro', fr: 'Pro' },
    features: {
      en: ['All Basic benefits', 'Verified badge on public profile', 'Shared Data Room', 'Mandate analytics', 'Visibility on deals in your sector'],
      fr: ['Tout Basic', 'Badge vérifié sur profil public', 'Data Room partagée', 'Analytics mandats', 'Visibilité sur les deals de votre secteur'],
    },
  },
  elite: {
    priceId: 'price_1TcziFCkUJGKtqQAyp4YLDL0',
    price: 349,
    regularPrice: 499,
    name: { en: 'Elite', fr: 'Elite' },
    features: {
      en: ['All Pro benefits', 'Profile featured on ALL relevant listings', '1 Market Study included/quarter', 'Dedicated AcquizGroup support', 'Priority on buyer contact requests'],
      fr: ['Tout Pro', 'Profil mis en avant sur TOUS les listings pertinents', '1 Market Study inclus/trimestre', 'Support dédié AcquizGroup', 'Priorité sur les demandes de contact acheteurs'],
    },
  },
} as const;

export type PartnerPlanKey = keyof typeof PARTNER_PLANS;
export function computePartnerMonthlyTotal(addonsCount: number): number {
  return PARTNER_BASE_AMOUNT_USD + addonsCount * PARTNER_ADDON_AMOUNT_USD;
}

export interface CreatePartnerCheckoutResult {
  url: string;
  sessionId: string;
}

export async function createPartnerCheckout(
  addons: PartnerAddonSlug[],
  plan: PartnerPlanKey = 'basic',
  args?: {
    origin?: string;
    successPath?: string;
    cancelPath?: string;
  }
): Promise<CreatePartnerCheckoutResult> {
  const user = auth.currentUser;
  if (!user) throw new Error("Login required");
  const idToken = await user.getIdToken();
  const res = await fetch(`${API_CONFIG.baseURL}/payments/partner-checkout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ addons, baseTier: plan, ...args }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Partner checkout failed (${res.status}): ${text || res.statusText}`
    );
  }
  return (await res.json()) as CreatePartnerCheckoutResult;
}


export interface UpdatePartnerAddonsPreview {
  ok: true;
  preview: true;
  amountDueToday: number; // cents
  currency: string;
  nextBillingAmount: number; // cents
  nextBillingDate: string | null;
  prorationLines: Array<{
    description: string | null;
    amount: number;
    priceId: string | null;
  }>;
  hasAddition: boolean;
  hasRemoval: boolean;
}

export interface UpdatePartnerAddonsApply {
  ok: true;
  preview: false;
  subscriptionId: string;
  status: string;
  hasAddition: boolean;
  hasRemoval: boolean;
}

export interface UpdatePartnerAddonsUnchanged {
  ok: true;
  unchanged: true;
  message: string;
}

export type UpdatePartnerAddonsResult =
  | UpdatePartnerAddonsPreview
  | UpdatePartnerAddonsApply
  | UpdatePartnerAddonsUnchanged;

export async function updatePartnerAddons(args: {
  targetAddons: PartnerAddonSlug[];
  preview?: boolean;
}): Promise<UpdatePartnerAddonsResult> {
  const user = auth.currentUser;
  if (!user) throw new Error("Login required");
  const idToken = await user.getIdToken();
  const res = await fetch(`${API_CONFIG.baseURL}/payments/partner-addons`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Update addons failed (${res.status}): ${text || res.statusText}`);
  }
  return (await res.json()) as UpdatePartnerAddonsResult;
}

export interface SyncPartnerSubscriptionResult {
  ok: true;
  ready: boolean;
  status: string;
  monthlyTotal?: number;
  addons?: PartnerAddonSlug[];
}

/**
 * Fallback client-side : finalise l'abonnement Partenaire sans dépendre
 * du webhook Stripe. Appelé en boucle (polling) depuis la page success
 * jusqu'à ce que `ready === true`.
 */
export async function syncPartnerSubscription(args: {
  sessionId: string;
}): Promise<SyncPartnerSubscriptionResult> {
  const fn = httpsCallable<typeof args, SyncPartnerSubscriptionResult>(
    functions,
    "syncPartnerSubscription"
  );
  const { data } = await fn(args);
  return data;
}
